import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { isPasswordValid } from './PasswordProtect';
import { Helmet } from 'react-helmet-async'; // Import Helmet
import axios from 'axios';
import { CartContext, EXTRA_SCRATCH_PRICE_CENTS } from '../context/CartContext'; 
import { trackEvent } from './TrackingProvider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Chip from '@mui/material/Chip';

import { getLabelProperties } from './LabelEtiquette';

import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import IconButton from '@mui/material/IconButton';
import ReactMarkdown from 'react-markdown';

import { resolveMediaUrl, getDemoPath, getVisuelPath } from '../utils/media';
import { markdownToExcerpt } from '../utils/text';
import ProductOptionConfigurator from './ProductOptionConfigurator';
import MinisiteView from './MinisiteView';
import { useMinisiteOccasions, useFicheDemo, useFicheDefaults } from '../utils/minisiteOccasions';
import { API_BASE } from '../utils/apiBase';
import { MinisiteArguments, MinisiteVideo, MinisiteContenu } from './MinisiteCatalogPage';
import Faq from './Faq';
import { titreSeo, alternatesFor } from './Seo';
import { useT, useLang, absoluteUrl, DEFAULT_LANG } from '../i18n';
import { useProductTexts, useProductLongDescription } from '../utils/productI18n';

const SITE_URL = 'https://joli-mot.fr';

// Une carte est grattable 2 fois par défaut (cf. cartes_use.max_scratch_allowed côté API).
// Grattages supplémentaires : option payante pour les cartes envoyées à plusieurs
// destinataires qui veulent chacun pouvoir tenter leur chance.
const BASE_SCRATCH_ALLOWANCE = 2;
const MAX_EXTRA_SCRATCHES = 20;
// Titre visible (au-dessus du cœur, jamais caché) : distinct du message secret ci-dessus.
// Court par nature (affiché en une ou deux lignes sur le fond), proposé seulement sur les
// visuels marqués config_carte.titre_personnalisable = 1.
const TITRE_MAX_LENGTH = 24;
// Longueur maximale du message secret (attribut maxLength du champ et compteur affiché).
const SECRET_MAX_LENGTH = 150;

// Données structurées « Product » : un SEUL bloc dans <head>, remplacé quand son contenu change (titre puis
// description traduits qui arrivent). Posé à la main plutôt que par <Helmet> : React Helmet laissait s'accumuler
// les anciennes versions d'un <script> dont le contenu change pendant la vie de la page.
const ProductJsonLd = ({ json }) => {
  useEffect(() => {
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.setAttribute('data-produit', 'true');
    el.textContent = json;
    document.head.appendChild(el);
    return () => { el.remove(); };
  }, [json]);
  return null;
};

const ProductDetail = () => {
  const t = useT('product-detail');
  const { lang } = useLang();
  // Exemple de la démo en direct, dans la langue de la page : celui de l'occasion, puis celui de la fiche.
  const occasions = useMinisiteOccasions();
  const ficheDemo = useFicheDemo();
  const ficheDefaults = useFicheDefaults();
  const tp = useProductTexts(); // titre / étiquette traduits (repli : français)
  const { url } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useContext(CartContext);
  const [product, setProduct] = useState(null);
  const [customText, setCustomText] = useState('');
  const [customTitre, setCustomTitre] = useState('');
  const [selectedVisual, setSelectedVisual] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [extraScratches, setExtraScratches] = useState(0);
  const [textError, setTextError] = useState(false);
  // Produit configurable générique (mini-site de mariage et futurs produits à options) :
  // choix faits dans ProductOptionConfigurator, piloté par product.option_schema.
  const [configuredSelections, setConfiguredSelections] = useState({});
  const [configuredDeltaCents, setConfiguredDeltaCents] = useState(0);
  // SKU (config_carte.visuel) -> titre personnalisable autorisé, chargé une fois via
  // adminListVisuals (le snapshot produits_local.visuels n'a pas ce flag, cf. ROADMAP).
  const [titrePersonnalisableParSku, setTitrePersonnalisableParSku] = useState({});
  const VAT_RATE = 0.2;
  // Description longue traduite (fichier statique) ou null : on affiche alors la description française.
  const longDescription = useProductLongDescription(product);

  useEffect(() => {
    axios.get(`${API_BASE}/api-2026.php`, {
      params: { 
        cleApi: '34KVHFQSP226YTk4n256QP6rGbqhA5w2ymY7W3pLwdbuc3i75',
        service: 'getProductByUrl',
        url: url
      }
    })
      .then(response => {
        const productData = response.data.data;
        if (productData) {
          setProduct(productData);
          const firstVisuel = productData.visuels?.[0];
          setSelectedVisual(firstVisuel?.url || getVisuelPath(firstVisuel?.visuel) || productData.featured_image || '');
        } else {
          navigate('/404');
        }
      })
      .catch(error => {
        console.error('Erreur lors du chargement du produit:', error);
      });
  }, [url, navigate]);

  // Charge le flag titre_personnalisable par SKU de visuel (pas dans le snapshot produit).
  useEffect(() => {
    axios.get(`${API_BASE}/api-2026.php`, {
      params: { cleApi: '34KVHFQSP226YTk4n256QP6rGbqhA5w2ymY7W3pLwdbuc3i75', service: 'adminListVisuals' }
    })
      .then(response => {
        const list = response.data?.data || [];
        const map = {};
        list.forEach(v => { map[v.visuel] = Number(v.titre_personnalisable) === 1; });
        setTitrePersonnalisableParSku(map);
      })
      .catch(error => {
        console.error('Erreur lors du chargement des visuels:', error);
      });
  }, []);

  // Tracker la vue du produit
  useEffect(() => {
    if (product) {
      trackEvent('product_view', {
        category: 'Products',
        label: product.titre,
        product_id: product.idProd
      });
    }
  }, [product]);

  // Produit générique piloté par option_schema (mini-site de mariage, cf.
  // migration-minisite-mariage.sql) : aucun message secret, aucun grattage, le prix et les
  // choix viennent du configurateur (ProductOptionConfigurator).
  // Une fiche "composition" de mini-site a un visuel figé : product_type + defaults, sans option.
  const isConfigurable = !!product?.option_schema?.product_type
    || (Array.isArray(product?.option_schema?.options) && product.option_schema.options.length > 0);
  // La fiche ne fait choisir que le visuel ; `defaults` porte la "composition" de la fiche
  // (police, couleur…), modifiable ensuite par le client dans l'éditeur (MinisiteEditor.js).
  const configuredParams = { ...ficheDefaults(product?.option_schema?.defaults || {}), ...configuredSelections };
  // 'minisite' = faire-part / invitation animé(e) ; 'galerie' = galerie photos des invités (sans éditeur).
  const productType = product?.option_schema?.product_type || 'minisite';

  const handleAddToCart = () => {
    if (!product) return;

    if (isConfigurable) {
      const priceEuros = (parseFloat(product.prix_ttc) || 0) || (parseFloat(product.prix_ht) || 0) * (1 + VAT_RATE);
      const basePriceCents = Math.round(priceEuros * 100);
      const totalPriceCents = basePriceCents + configuredDeltaCents;
      addToCart({
        id: product.id,
        name: product.titre,
        // Adresse du produit : le panier s'en sert pour afficher le nom traduit ; le nom enregistré reste le français.
        url: product.url,
        price: totalPriceCents,
        basePrice: basePriceCents,
        // Discriminant lu par CheckoutForm.js (prepareCardsForAPI) pour construire l'objet
        // envoyé à apiEtsy.php::addOrder avec product_type='minisite' + params, au lieu du
        // chemin carte à gratter habituel.
        productType,
        // Langue du site au moment de l'achat : proposée comme langue de la page du faire-part (réglage `langue`,
        // modifiable ensuite dans l'éditeur). Rien de plus en français : la commande reste identique.
        minisiteParams: lang !== DEFAULT_LANG ? { ...configuredParams, langue: lang } : configuredParams,
        optionsDeltaCents: configuredDeltaCents,
        // visuelSku = le fond acheté (envoyé à addOrder). L'image du panier, elle, est celle de la
        // fiche (featuredImage) : la démo d'un visuel est une carte à gratter, pas un faire-part.
        visuelSku: configuredParams.theme_visuel || '',
        featuredImage: product.featured_image || '',
      });

      window.scrollTo({ top: 0, behavior: 'smooth' });
      toast.success(t('product-detail.toast.added', { title: tp(product).titre }), {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      trackEvent('add_to_cart', {
        category: 'E-commerce',
        label: product.titre,
        value: product.prix_ttc || product.prix_ht,
        product_id: product.idProd,
      });
      return;
    }

    if (customText.trim() === '') {
      // Sans message, pas de carte : on le dit clairement au lieu d'ignorer le clic.
      setTextError(true);
      toast.warning(t('product-detail.toast.secretMissing'), {
        position: 'top-right', autoClose: 4000,
      });
      document.getElementById('texte-personnalisation')?.focus();
      return;
    }
    {
      // prix_ttc est en euros en base ; le panier stocke des centimes
      const priceEuros = (parseFloat(product.prix_ttc) || 0) || (parseFloat(product.prix_ht) || 0) * (1 + VAT_RATE);
      const basePriceCents = Math.round(priceEuros * 100);
      const totalPriceCents = basePriceCents + extraScratches * EXTRA_SCRATCH_PRICE_CENTS;
      // SKU du visuel sélectionné (champ config_carte.visuel) — indispensable pour créer la carte
      const selIdx = visualPaths.indexOf(selectedVisual);
      const visuelSku = visuelsData[selIdx >= 0 ? selIdx : 0]?.visuel;
      // Titre visible : envoyé seulement si le visuel choisi l'autorise (évite d'envoyer un
      // titre resté rempli après un changement vers un visuel non personnalisable).
      const titrePersoActifPourEnvoi = !!titrePersonnalisableParSku[visuelSku];
      addToCart({
        id: product.id,
        name: product.titre,
        url: product.url, // voir plus haut : nom traduit dans le panier
        price: totalPriceCents,
        basePrice: basePriceCents,
        customText,
        customTitre: titrePersoActifPourEnvoi ? customTitre.trim() : '',
        selectedVisual,
        visuelSku,
        featuredImage: selectedVisual,
        // Nombre de grattages autorisés pour la carte créée (2 de base + options payantes) —
        // lu par CheckoutForm.js pour renseigner cartes_use.max_scratch_allowed à la création.
        extraScratches,
        maxScratchAllowed: BASE_SCRATCH_ALLOWANCE + extraScratches,
      });

      // Faire défiler vers le haut de la page
      window.scrollTo({ top: 0, behavior: 'smooth' });

      toast.success(t('product-detail.toast.added', { title: tp(product).titre }), {
        position: "top-right", // Changer la position en haut à droite
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });

      // Tracker l'ajout au panier
      trackEvent('add_to_cart', {
        category: 'E-commerce',
        label: product.titre,
        value: product.prix_ttc || product.prix_ht,
        product_id: product.idProd
      });
    }
  };

  const handleVisualSelection = (index, visualPath) => {
    setSelectedVisual(visualPath);
    setCurrentSlide(index);
  };

  const isAdmin = isPasswordValid();

  if (!product) return <p>{t('product-detail.loading')}</p>;

  const prixTTC = parseFloat(product.prix_ttc) || 0;
  const prixHT = parseFloat(product.prix_ht) || 0;
  const priceTTC = prixTTC || (prixHT * (1 + VAT_RATE)) || 0;
  // Produit affiché : titre (et étiquette) traduits si un fichier de traduction existe, sinon le français.
  const shown = tp(product);
  const isTranslated = shown !== product;

  const visuelsData = (product.visuels && product.visuels.length > 0)
    ? product.visuels
    : (product.featured_image ? [{ url: product.featured_image }] : []);
  // Paths used for cart/order (raw visuel, needed for SKU extraction) — never displayed
  const visualPaths = visuelsData.map(v => v.url || getVisuelPath(v.visuel));
  // Demo images for display. Deux cas selon la forme de l'item (voir visuelsData ci-dessus) :
  //  - item de config_carte ({ visuel, url, medium }) : uniquement la démo dérivée de `visuel`,
  //    `url` (le fond brut) n'est JAMAIS affiché ; si la démo est absente, l'image est masquée.
  //  - repli featured_image ({ url } sans `visuel`, produit sans aucun visuel rattaché) :
  //    `url` est une image de démo/lifestyle déjà prête, c'est la seule source disponible.
  const demoImages = visuelsData.map(v => v.visuel ? getDemoPath(v.visuel) : v.url);
  // Le visuel actuellement sélectionné autorise-t-il un titre visible personnalisable ?
  const selectedVisualIdx = visualPaths.indexOf(selectedVisual);
  const selectedVisuelSku = visuelsData[selectedVisualIdx >= 0 ? selectedVisualIdx : 0]?.visuel;
  const titrePersonnalisableActif = !!titrePersonnalisableParSku[selectedVisuelSku];
  const handleDemoError = (e) => {
    e.target.style.visibility = 'hidden';
    e.target.onerror = null;
  };

  return (
    <>
      {/* ── Badge admin Éditer ────────────────────────────── */}
      {isAdmin && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1300,
          }}
        >
          <Button
            variant="contained"
            color="secondary"
            startIcon={<EditIcon />}
            onClick={() => navigate('/gestion-catalogue', { state: { editProduct: product } })}
            sx={{
              borderRadius: 6,
              boxShadow: 4,
              fontWeight: 700,
              px: 2.5,
              py: 1.2,
              '&:hover': { boxShadow: 6 },
            }}
          >
            Éditer ce produit
          </Button>
        </Box>
      )}

     {/* SEO par fiche : titre, description propre (sans markdown), canonical, Open Graph avec la
         démo du 1er visuel, et données structurées Product pour Google (ROADMAP, A3.2). */}
     {(() => {
        // Adresse de la fiche dans la langue courante (préfixe /en, /de… sauf en français) + versions par langue (hreflang).
        const productPath = `/produit/${product.url}`;
        const pageUrl = absoluteUrl(productPath, lang);
        // Fiche traduite : titre et description dans la langue (les méta saisies en base sont françaises et ne servent
        // qu'au français, ou à une fiche pas encore traduite, qui reste alors entièrement en français).
        const seoTitle = isTranslated
          ? (isConfigurable ? titreSeo(shown.titre) : titreSeo(shown.titre, ` | ${t('product-detail.seo.cardSuffix')}`))
          : (product.meta_title
            || (isConfigurable ? titreSeo(product.titre) : titreSeo(product.titre, ' | Carte à gratter numérique')));
        const seoDesc = isTranslated
          ? (markdownToExcerpt(longDescription, 155)
            || markdownToExcerpt(shown.description_courte, 155)
            || t('product-detail.seo.descriptionFallback', { title: shown.titre }))
          : (product.meta_description
            || markdownToExcerpt(product.description, 155)
            || `${product.titre} : carte à gratter numérique personnalisée, message secret révélé au grattage, envoi instantané par lien.`);
        const ogImage = demoImages[0] ? resolveMediaUrl(demoImages[0]).replace(/^(?!https?:)/, SITE_URL) : `${SITE_URL}/logo512.png`;
        const jsonLd = {
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: shown.titre,
          description: seoDesc,
          image: ogImage,
          url: pageUrl,
          brand: { '@type': 'Brand', name: 'Joli Ptit Mot' },
          offers: {
            '@type': 'Offer',
            price: priceTTC.toFixed(2),
            priceCurrency: 'EUR',
            availability: 'https://schema.org/InStock',
            url: pageUrl,
          },
        };
        return (
          <>
          <Helmet>
            <title>{seoTitle}</title>
            <meta name="description" content={seoDesc} />
            <link rel="canonical" href={pageUrl} />
            {alternatesFor(productPath)}
            <meta name="robots" content="index, follow" />
            <meta property="og:type" content="product" />
            <meta property="og:title" content={seoTitle} />
            <meta property="og:description" content={seoDesc} />
            <meta property="og:url" content={pageUrl} />
            <meta property="og:image" content={ogImage} />
            <meta name="twitter:card" content="summary_large_image" />
          </Helmet>
          <ProductJsonLd json={JSON.stringify(jsonLd)} />
          </>
        );
      })()}
      <Grid container spacing={4} sx={{ marginTop: 0, marginBottom: 0 }}>
        {/* Produit à options (mini-site de mariage) : à la place du carrousel de mockups
            carte-à-gratter, une démo live du mini-site avec des informations d'exemple, mise à
            jour à chaque changement de thème/police/couleur/options. Conditionnée à la présence
            de theme_visuel plutôt qu'au produit précis, pour rester réutilisable par un futur
            produit à option_schema qui partagerait ce vocabulaire d'options. */}
        {isConfigurable && !configuredParams.theme_visuel && product.featured_image && (
          <Grid item xs={12} md={6}>
            <img src={resolveMediaUrl(product.featured_image)} alt={shown.titre} style={{ width: '100%', borderRadius: 8 }} />
          </Grid>
        )}
        {isConfigurable && configuredParams.theme_visuel && (
          <Grid item xs={12} md={6}>
            <Box sx={{ position: { md: 'sticky' }, top: { md: 24 } }}>
              <div className="ms-phone">
                {/* Exemple : celui de l'occasion, puis celui de CE modèle (option_schema.demo : prénom,
                    ligne d'âge, texte), puis les réglages de la fiche. */}
                <MinisiteView embedded params={{ ...(occasions[configuredParams.occasion] || occasions.mariage).demo, carte_lieu: false, ...(ficheDemo(configuredParams.occasion || 'mariage', product.option_schema?.demo) || {}), ...configuredParams }} />
              </div>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', marginTop: 1 }}>
                {t('product-detail.liveDemo')}
              </Typography>
            </Box>
          </Grid>
        )}
        {!isConfigurable && (
          <Grid item xs={12} md={6}>
            <Carousel
              showThumbs={false}
              infiniteLoop={true}
              dynamicHeight={false}
              selectedItem={currentSlide}
              onChange={(index) => handleVisualSelection(index, visualPaths[index])}
              statusFormatter={(current, total) => t('product-detail.gallery.status', { current, total })}
              labels={{ leftArrow: t('product-detail.gallery.previous'), rightArrow: t('product-detail.gallery.next'), item: t('product-detail.gallery.item') }}
            >
              {demoImages.map((src, index) => (
                <div key={index}>
                  <img
                    src={resolveMediaUrl(src)}
                    alt={t('product-detail.gallery.productAlt', { n: index + 1 })}
                    style={{ borderRadius: '4px' }}
                    onError={handleDemoError}
                  />
                </div>
              ))}
            </Carousel>
          </Grid>
        )}

        <Grid item xs={12} md={6} sx={{ position: 'relative' }}>
          {product.etiquette && (() => {
              // Couleur et icône : d'après l'étiquette française de la base ; texte : l'étiquette traduite.
              const { backgroundColor, icon } = getLabelProperties(product.etiquette);
              
              return (
                  <Chip
                      label={shown.etiquette}
                      style={{ backgroundColor, color: '#FFFFFF' }}
                      icon={icon}
                      sx={{ position: 'absolute', top: -20, right: 0 }}
                  />
              );
          })()}

          {/* h1 : le titre de la fiche est LE titre de la page (même rendu, la classe titreProd prime). */}
          <Typography component="h1" color="primary" gutterBottom className='titreProd'>
            {shown.titre}
          </Typography>

          <Typography color="secondary" gutterBottom className='prixProd'>
            {t('product-detail.price', { amount: priceTTC })}
          </Typography>

          {isConfigurable ? (
            // Produit générique à options (mini-site de mariage, cf. option_schema) : aucun
            // champ carte à gratter, tout vient du configurateur piloté par le schema.
            <>
              <ProductOptionConfigurator
                optionSchema={product.option_schema}
                onChange={(selections, deltaCents) => {
                  setConfiguredSelections(selections);
                  setConfiguredDeltaCents(deltaCents);
                }}
              />
              {productType === 'minisite' && (
                <>
                  <Typography variant="body2" sx={{ marginTop: 3 }}>
                    {product.option_schema.options?.length > 0 ? t.rich('product-detail.editorNote.choose') : t.rich('product-detail.editorNote.start')}
                  </Typography>
                </>
              )}
            </>
          ) : (
            <>
              <Box sx={{ marginTop: 3 }}>
                <TextField
                  id="texte-personnalisation"
                  label={t('product-detail.secret.label')}
                  variant="outlined"
                  fullWidth
                  required
                  multiline
                  rows={3}
                  inputProps={{ maxLength: SECRET_MAX_LENGTH }}
                  value={customText}
                  onChange={(e) => { setCustomText(e.target.value); if (textError && e.target.value.trim()) setTextError(false); }}
                  error={textError}
                  helperText={textError
                    ? t('product-detail.secret.error')
                    : t('product-detail.secret.help', { current: customText.length, max: SECRET_MAX_LENGTH })}
                />
              </Box>

              {titrePersonnalisableActif && (
                <Box sx={{ marginTop: 3 }}>
                  <TextField
                    id="titre-visible-personnalisation"
                    label={t('product-detail.visibleTitle.label')}
                    variant="outlined"
                    fullWidth
                    inputProps={{ maxLength: TITRE_MAX_LENGTH }}
                    value={customTitre}
                    onChange={(e) => setCustomTitre(e.target.value)}
                    helperText={t('product-detail.visibleTitle.help', { current: customTitre.length, max: TITRE_MAX_LENGTH })}
                  />
                </Box>
              )}

              <Box sx={{ marginTop: 3 }}>
                <Typography variant="h6">{t('product-detail.gallery.chooseVisual')}</Typography>
                <Grid container spacing={2} sx={{ marginTop: 1 }}>
                  {demoImages.map((demoSrc, index) => (
                    <Grid item xs={3} key={index}>
                      <Paper
                        elevation={selectedVisual === visualPaths[index] ? 4 : 1}
                        sx={{
                          cursor: 'pointer',
                          border: selectedVisual === visualPaths[index] ? '2px solid #c48f8b' : 'none',
                          padding: 1,
                          textAlign: 'center',
                        }}
                        onClick={() => handleVisualSelection(index, visualPaths[index])}
                      >
                        <img
                          src={resolveMediaUrl(demoSrc)}
                          alt={t('product-detail.gallery.visualAlt', { n: index + 1 })}
                          style={{ width: '100%', borderRadius: '4px' }}
                          onError={handleDemoError}
                        />
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <Box sx={{ marginTop: 3 }}>
                <Typography variant="h6">{t('product-detail.extras.title')}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ marginTop: 0.5 }}>
                  {t('product-detail.extras.description', { allowance: BASE_SCRATCH_ALLOWANCE, price: EXTRA_SCRATCH_PRICE_CENTS / 100 })}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, marginTop: 1.5 }}>
                  <IconButton
                    aria-label={t('product-detail.extras.remove')}
                    onClick={() => setExtraScratches((n) => Math.max(0, n - 1))}
                    disabled={extraScratches <= 0}
                    sx={{ border: '1px solid #e0d5d3' }}
                    size="small"
                  >
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                  <Typography sx={{ minWidth: 24, textAlign: 'center', fontWeight: 600 }}>
                    {extraScratches}
                  </Typography>
                  <IconButton
                    aria-label={t('product-detail.extras.add')}
                    onClick={() => setExtraScratches((n) => Math.min(MAX_EXTRA_SCRATCHES, n + 1))}
                    disabled={extraScratches >= MAX_EXTRA_SCRATCHES}
                    sx={{ border: '1px solid #e0d5d3' }}
                    size="small"
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                  {extraScratches > 0 && (
                    <Typography variant="body2" color="secondary" sx={{ fontWeight: 600 }}>
                      {t('product-detail.extras.summary', { amount: (extraScratches * EXTRA_SCRATCH_PRICE_CENTS) / 100, total: BASE_SCRATCH_ALLOWANCE + extraScratches })}
                    </Typography>
                  )}
                </Box>
              </Box>
            </>
          )}

          <Divider sx={{ marginY: 2 }} />

          {/* Prix affiché en clair tout de suite, pour qu'il n'y ait aucune question sur le
              coût une fois les options ajoutées. */}
          <Typography variant="h6" sx={{ marginBottom: 1 }}>
            {t('product-detail.total', { amount: priceTTC + (isConfigurable ? configuredDeltaCents : extraScratches * EXTRA_SCRATCH_PRICE_CENTS) / 100 })}
          </Typography>

          <Button
            variant="contained"
            color="secondary"
            onClick={handleAddToCart}
            sx={{ marginTop: 1 }}
          >
            {t('product-detail.addToCart')}
          </Button>

          {/* Sous le bouton d'achat, pour qu'il reste visible sans faire défiler la page. */}
          {isConfigurable && productType === 'minisite' && (
            <>
              <Box sx={{ marginTop: 3 }}><MinisiteVideo compact /></Box>
              <Box sx={{ marginTop: 2 }}><MinisiteContenu occasionKey={configuredParams.occasion || 'mariage'} /></Box>
              <Box sx={{ marginTop: 2 }}><MinisiteArguments /></Box>
            </>
          )}

        </Grid>
      </Grid>


      <Typography variant="h5" sx={{ marginTop: 4 }}>
        {t('product-detail.detailsTitle')}
      </Typography>

      {/* Description rendue directement en markdown. L'ancien découpage en accordéons
          par titre exigeait un format "### **titre**" que les descriptions en base ne
          suivent pas : la section restait vide sur toutes les fiches (A2.4). */}
      {(longDescription || product.description) ? (
        <Box
          sx={{
            maxWidth: 820,
            color: '#4a3a3a',
            lineHeight: 1.7,
            '& h1, & h2, & h3, & h4': {
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              color: '#c48f8b',
              fontWeight: 600,
              marginTop: 3,
              marginBottom: 1,
              fontSize: '1.35rem',
            },
            '& p': { marginBottom: 1.5 },
            '& img': { maxWidth: '100%', borderRadius: 2 },
          }}
        >
          <ReactMarkdown
            components={{
              // Le titre de la fiche est déjà le <h1> : un « # » de la description devient un <h2>.
              h1: ({ node, ...props }) => <h2 {...props} />,
              img: ({ node, src, alt, ...props }) => (
                <img src={resolveMediaUrl(src)} alt={alt || ''} {...props} />
              ),
            }}
          >
            {longDescription || product.description}
          </ReactMarkdown>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t('product-detail.emptyDescription')}
        </Typography>
      )}

      <Faq page={!isConfigurable ? 'produit-carte' : productType === 'galerie' ? 'produit-galerie' : `occ-${configuredParams.occasion || 'mariage'}`} />

      <ToastContainer />
    </>
  );
};

export default ProductDetail;
